import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PatientSearchField from "sections/components-overview/PatientSearchField";
import { sendReport } from "utils/apiHelper";
import { isAutoWhatsAppEnabled } from "utils/autoWhatsAppPreferences";
import { openSnackbar } from "api/snackbar";
import CircularProgress from "@mui/material/CircularProgress";
import PdfCaptureContext from "contexts/PdfCaptureContext";

const SendReportSection = ({
  reportId,
  generatePdfBlob,
  patientId,
  patientName,
  autoSendWhatsAppKey = null,
  onAutoSendComplete,
}) => {
  const [emailLoading, setEmailLoading] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);

  // instead of formik
  const [formValues, setFormValues] = useState({
    name: patientName || "",
    patientId: patientId || null
  });

  useEffect(() => {
    setFormValues({
      name: patientName || "",
      patientId: patientId || null
    });
  }, [patientId, patientName]);
  
  const pdfBlobRef = useRef(null);
  const pdfPromiseRef = useRef(null);
  const sendPromisesRef = useRef({});
  const consumedAutoSendKeysRef = useRef(new Set());
  const generatePdfBlobRef = useRef(generatePdfBlob);
  const onAutoSendCompleteRef = useRef(onAutoSendComplete);

  generatePdfBlobRef.current = generatePdfBlob;
  onAutoSendCompleteRef.current = onAutoSendComplete;

  useEffect(() => {
    pdfBlobRef.current = null;
    pdfPromiseRef.current = null;
    sendPromisesRef.current = {};
  }, [reportId]);

  const generatePdfOnce = useCallback(async () => {
    if (pdfBlobRef.current) return pdfBlobRef.current;
    if (pdfPromiseRef.current) return pdfPromiseRef.current;

    const pdfPromise = (async () => {
      const blob = await generatePdfBlobRef.current();
      if (!blob) throw new Error("PDF generation failed");
      pdfBlobRef.current = blob;
      return blob;
    })();
    pdfPromiseRef.current = pdfPromise;

    try {
      return await pdfPromise;
    } finally {
      pdfPromiseRef.current = null;
    }
  }, []);

  const capturePdfGenerator = useContext(PdfCaptureContext);
  useEffect(() => {
    if (capturePdfGenerator && reportId) capturePdfGenerator(generatePdfOnce);
  }, [capturePdfGenerator, reportId, generatePdfOnce]);

  const sendReportFile = useCallback(async ({ method, userId, successMessage }) => {
    if (!userId || !reportId) {
      throw new Error("Patient and report details are required");
    }
    if (sendPromisesRef.current[method]) {
      return sendPromisesRef.current[method];
    }

    const sendPromise = (async () => {
      if (method === "whatsapp") setWhatsappLoading(true);
      if (method === "email") setEmailLoading(true);

      const file = await generatePdfOnce();
      await sendReport({ userId, method, reportId, file });
      openSnackbar({
        open: true,
        message: successMessage,
        variant: "alert",
        alert: { color: "success" },
      });
    })();
    sendPromisesRef.current[method] = sendPromise;

    try {
      return await sendPromise;
    } finally {
      delete sendPromisesRef.current[method];
      if (method === "whatsapp") setWhatsappLoading(false);
      if (method === "email") setEmailLoading(false);
    }
  }, [generatePdfOnce, reportId]);

  useEffect(() => {
    if (!autoSendWhatsAppKey) return;

    const key = String(autoSendWhatsAppKey);
    if (consumedAutoSendKeysRef.current.has(key)) return;

    if (!patientId || !reportId) {
      consumedAutoSendKeysRef.current.add(key);
      openSnackbar({
        open: true,
        message: "Unable to send report on WhatsApp: patient details are missing",
        variant: "alert",
        alert: { color: "error" },
      });
      onAutoSendCompleteRef.current?.(false);
      return;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      if (consumedAutoSendKeysRef.current.has(key)) return;
      let autoSendEnabled = true;
      try {
        autoSendEnabled = await isAutoWhatsAppEnabled(key);
      } catch (error) {
        console.error("Unable to load automatic WhatsApp preferences", error);
      }
      if (cancelled || consumedAutoSendKeysRef.current.has(key)) return;
      consumedAutoSendKeysRef.current.add(key);
      if (!autoSendEnabled) {
        onAutoSendCompleteRef.current?.(true);
        return;
      }
      sendReportFile({
        method: "whatsapp",
        userId: patientId,
        successMessage: "Report sent to patient on WhatsApp",
      })
        .then(() => onAutoSendCompleteRef.current?.(true))
        .catch((error) => {
          console.error("Automatic WhatsApp send failed", error);
          openSnackbar({
            open: true,
            message: "Failed to send report to patient on WhatsApp",
            variant: "alert",
            alert: { color: "error" },
          });
          onAutoSendCompleteRef.current?.(false);
        });
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [autoSendWhatsAppKey, patientId, reportId, sendReportFile]);

  const handleDownload = async () => {
    try {
      const pdfBlob = await generatePdfOnce();
  
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "report.pdf";
      document.body.appendChild(link);
      link.click();
  
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
    }
  };

  const handleSendEmail = async () => {
    try {
      await sendReportFile({
        method: "email",
        userId: formValues.patientId,
        successMessage: "Report sent successfully via Email",
      });
    } catch (error) {
      console.error("Email send failed", error);
  
      openSnackbar({
        open: true,
        message: "Failed to send report via Email -- " + error,
        variant: "alert",
        alert: { color: "error" },
      });
    }
  };

  const handleSendWhatsApp = async () => {
    try {
      await sendReportFile({
        method: "whatsapp",
        userId: formValues.patientId,
        successMessage: "Report sent successfully via WhatsApp",
      });
    } catch (error) {
      console.error("WhatsApp send failed", error);
  
      openSnackbar({
        open: true,
        message: "Failed to send report via WhatsApp -- " + error,
        variant: "alert",
        alert: { color: "error" },
      });
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", mx: { xs: 0, sm: 1.5 }, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1.5 }, minWidth: 0, width: { xs: "100%", sm: 360 } }}>
          <Box flex={1} minWidth={0}>
            <PatientSearchField
              value={formValues.name}
              onChange={(value) => {
                setFormValues(prev => ({ ...prev, name: value }));
                if (!value) {
                  setFormValues(prev => ({ ...prev, patientId: null }));
                }
              }}
              onPatientSelect={(patient) => {
                setFormValues({
                  name: patient.full_name,
                  patientId: patient.id
                });
              }}
              placeholder="Search User"
            />
          </Box>

          <Tooltip title="Send via Email" arrow>
            <span>
              <IconButton
                onClick={handleSendEmail}
                disabled={!formValues.patientId || emailLoading}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: formValues.patientId ? "#424242" : "#9e9e9e",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: formValues.patientId ? "#2e2e2e" : "#9e9e9e",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#9e9e9e",
                    color: "#ffffff",
                    opacity: 1,
                  },
                }}
              >
                {emailLoading ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <EmailRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Send via WhatsApp" arrow>
            <span>
              <IconButton
                onClick={handleSendWhatsApp}
                disabled={!formValues.patientId || whatsappLoading}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: formValues.patientId ? "#25D366" : "#a5d6a7",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: formValues.patientId ? "#1ebe5d" : "#a5d6a7",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#a5d6a7",
                    color: "#ffffff",
                    opacity: 1,
                  },
                }}
              >
                {whatsappLoading ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <WhatsAppIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Download PDF" arrow>
            <span>
              <IconButton
                onClick={handleDownload}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "primary.main",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                }}
              >
                <DownloadRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
      </Box>
    </Box>
  );
};

export default SendReportSection;